import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
  Tooltip,
  TableSortLabel,
} from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import { getConfig } from '../services/config';

interface ModelRate {
  model: string;
  credits: number;
  usd: number;
}

interface ModelRatesTableProps {
  sampleInputTokens?: number;
  sampleOutputTokens?: number;
  title?: string;
  compact?: boolean;
}

type SortField = 'model' | 'credits' | 'usd';

export const ModelRatesTable: React.FC<ModelRatesTableProps> = ({
  sampleInputTokens = 100_000,
  sampleOutputTokens = 100_000,
  title = 'Model Pricing Information',
  compact = false,
}) => {
  const config = getConfig();
  const [models, setModels] = useState<ModelRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortField>('model');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    fetchModelRates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sampleInputTokens, sampleOutputTokens]);

  const fetchModelRates = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(
        `${config.apiBaseUrl}/api/v2/billing/model-rates?sample_input_tokens=${sampleInputTokens}&sample_output_tokens=${sampleOutputTokens}`
      );
      
      if (!res.ok) throw new Error('Failed to fetch model rates');
      
      const data = await res.json();
      setModels(data.models || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  const sortedModels = [...models].sort((a, b) => {
    let aVal: string | number = a[sortBy];
    let bVal: string | number = b[sortBy];
    
    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = (bVal as string).toLowerCase();
    }

    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  }).filter((model) => !model.model.toLowerCase().includes('embedding'));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box sx={{ width: '100%' }}>
      {!compact && (
        <Box sx={{ mb: 3 }}>
          {title && (
            <Typography variant="h6" gutterBottom>
              {title}
            </Typography>
          )}
          
          {/* Prominent Sample Explanation Card */}
          <Paper
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: '#f0f7ff',
              border: '1px solid #b3d9ff',
              borderRadius: 1,
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <InfoIcon sx={{ color: '#0066cc', mt: 0.5, flexShrink: 0 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Sample Usage Calculation
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  The pricing shown below is based on a{' '}
                  <strong>{(sampleInputTokens / 1000).toLocaleString()}k input tokens</strong> +{' '}
                  <strong>{(sampleOutputTokens / 1000).toLocaleString()}k output tokens</strong> request.
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.875rem' }}>
                  💡 Use this to understand relative pricing between models. Your actual cost depends on your
                  specific token usage.
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      )}
      
      <TableContainer component={Paper} sx={{ maxHeight: compact ? 400 : 600, overflow: 'auto' }}>
        <Table stickyHeader={!compact} size={compact ? 'small' : 'medium'}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'model'}
                  direction={sortBy === 'model' ? sortDir : 'asc'}
                  onClick={() => handleSort('model')}
                >
                  Model
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortBy === 'credits'}
                  direction={sortBy === 'credits' ? sortDir : 'asc'}
                  onClick={() => handleSort('credits')}
                >
                  <Tooltip title="AI Credits used for this sample usage">
                    <span>Credits</span>
                  </Tooltip>
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortBy === 'usd'}
                  direction={sortBy === 'usd' ? sortDir : 'asc'}
                  onClick={() => handleSort('usd')}
                >
                  <Tooltip title="Estimated USD cost (wholesale)">
                    <span>USD Cost</span>
                  </Tooltip>
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedModels.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  No models available
                </TableCell>
              </TableRow>
            ) : (
              sortedModels.map((model) => (
                <TableRow
                  key={model.model}
                  hover
                  sx={{
                    '&:hover': {
                      backgroundColor: '#f9f9f9',
                    },
                  }}
                >
                  <TableCell sx={{ fontWeight: 500 }}>
                    <Tooltip title={`Model: ${model.model}`}>
                      <span>{model.model}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        color: '#0066cc',
                        fontWeight: 500,
                      }}
                    >
                      {model.credits.toLocaleString('en-US', {
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                      })}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        color: '#009900',
                        fontWeight: 500,
                      }}
                    >
                      ${model.usd.toLocaleString('en-US', {
                        maximumFractionDigits: 4,
                        minimumFractionDigits: 4,
                      })}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ModelRatesTable;
